<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script>
import { computed, defineComponent, ref } from 'vue';

import {
  ViewportDrawingMode,
  ViewportToolMode,
} from '@/src/store/contours/types/tool-modes-type';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

const DRAWING_MODE_CONFIG = {
  [ViewportDrawingMode.Freehand]: {
    icon: '/icons/drawing-modes/freehand-drawing.svg',
    label: 'Freehand Drawing',
    value: ViewportDrawingMode.Freehand,
  },
  [ViewportDrawingMode.PointClick]: {
    icon: '/icons/drawing-modes/click-drawing.svg',
    label: 'Point Click Drawing',
    value: ViewportDrawingMode.PointClick,
  },
  [ViewportDrawingMode.Threshold]: {
    icon: '/icons/drawing-modes/threshold-drawing.svg',
    label: 'Threshold Drawing',
    value: ViewportDrawingMode.Threshold,
  },
  [ViewportDrawingMode.Nudge]: {
    icon: '/icons/drawing-modes/nudge-contour.svg',
    label: 'Nudge Contour',
    value: ViewportDrawingMode.Nudge,
  },
  [ViewportDrawingMode.None]: {
    icon: '',
    label: 'No Drawing Mode',
    value: ViewportDrawingMode.None,
  },
};

const TOOL_MODE_CONFIG = {
  [ViewportToolMode.WindowLevel]: {
    icon: '/icons/image-interaction/windowing.svg',
    label: 'Window/Level Tool',
    value: ViewportToolMode.WindowLevel,
  },
  [ViewportToolMode.Pan]: {
    icon: '/icons/image-interaction/panning.svg',
    label: 'Pan Tool',
    value: ViewportToolMode.Pan,
  },
  [ViewportToolMode.Zoom]: {
    icon: '/icons/image-interaction/zooming.svg',
    label: 'Zoom Tool',
    value: ViewportToolMode.Zoom,
  },
  [ViewportToolMode.ZoomSide]: {
    icon: '/icons/image-interaction/zooming.svg',
    label: 'Side Zoom Tool',
    value: ViewportToolMode.ZoomSide,
  },
  [ViewportToolMode.Rotate]: {
    icon: '/icons/image-interaction/rotation.svg',
    label: 'Rotation Tool',
    value: ViewportToolMode.Rotate,
  },
  [ViewportToolMode.Contour]: {
    icon: '/icons/image-interaction/drawing.svg',
    label: 'Contour Tool',
    value: ViewportToolMode.Contour,
  },
  [ViewportToolMode.None]: {
    icon: '',
    label: 'No Tool',
    value: ViewportToolMode.None,
  },
};

export default defineComponent({
  name: 'CornerMenu',
  props: {
    mode: {
      type: String,
      required: true,
      validator: value => ['drawing', 'tool'].includes(value),
    },
    viewportId: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: 'top-left',
      validator: value =>
        ['top-left', 'top-right', 'bottom-right', 'bottom-left'].includes(
          value,
        ),
    },
    radius: {
      type: Number,
      default: 100,
    },
    iconSize: {
      type: Number,
      default: 24,
    },
    innerRadiusRatio: {
      type: Number,
      default: 0.6,
    },
    defaultIcon: {
      type: String,
      default:
        'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'white\'%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'6\'/%3E%3C/svg%3E',
    },
  },

  setup(props) {
    const viewportStateStore = useViewportStateStore();

    const isOpen = ref(false);
    const showDrawingTools = computed(() => {
      if (
        props.mode === 'drawing'
        && viewportStateStore.viewportStates[props.viewportId]?.toolMode
        !== ViewportToolMode.Contour
      ) {
        return false;
      }
      return true;
    });

    const modeConfig = computed(
      () =>
        ({
          drawing: {
            config: DRAWING_MODE_CONFIG,
            storeGetter: () =>
              viewportStateStore.viewportStates[props.viewportId]?.drawingMode,
            storeSetter: value =>
              viewportStateStore.setDrawingMode(props.viewportId, value),
          },
          tool: {
            config: TOOL_MODE_CONFIG,
            storeGetter: () =>
              viewportStateStore.viewportStates[props.viewportId]?.toolMode,
            storeSetter: value =>
              viewportStateStore.setToolMode(props.viewportId, value),
          },
        })[props.mode],
    );

    // Generate items from config
    const items = computed(() => {
      const { config } = modeConfig.value;
      return Object.values(config).map(({ icon, label, value }) => ({
        icon,
        label,
        value,
      }));
    });

    const selectableItems = computed(() => {
      return items.value.filter(
        item =>
          item.value !== ViewportDrawingMode.None
          && item.value !== ViewportToolMode.None
          && item.value !== ViewportToolMode.ZoomSide
          && item.value !== ViewportToolMode.Contour,
      );
    });

    const selectedItem = computed(() => {
      const currentValue = modeConfig.value.storeGetter();
      return items.value.find(item => item.value === currentValue);
    });

    const currentDrawingContourIcon = computed(() => {
      return viewportStateStore.getDrawingContourProps(props.viewportId)?.icon;
    });

    const closeButtonRadius = computed(() => props.radius * 0.15);
    const innerRadius = computed(() => props.radius * props.innerRadiusRatio);

    const center = computed(() => {
      switch (props.position) {
        case 'top-left':
          return { x: 0, y: 0 };
        case 'top-right':
          return { x: props.radius, y: 0 };
        case 'bottom-right':
          return { x: props.radius, y: props.radius };
        case 'bottom-left':
          return { x: 0, y: props.radius };
        default:
          return { x: 0, y: 0 };
      }
    });

    const startAngleOffset = computed(() => {
      switch (props.position) {
        case 'top-left':
          return 0;
        case 'top-right':
          return Math.PI / 2;
        case 'bottom-right':
          return Math.PI;
        case 'bottom-left':
          return (3 * Math.PI) / 2;
        default:
          return 0;
      }
    });

    const closeButtonPosition = computed(() => {
      const offset = closeButtonRadius.value;
      switch (props.position) {
        case 'top-left':
          return { x: offset, y: offset };
        case 'top-right':
          return { x: props.radius - offset, y: offset };
        case 'bottom-right':
          return { x: props.radius - offset, y: props.radius - offset };
        case 'bottom-left':
          return { x: offset, y: props.radius - offset };
        default:
          return { x: offset, y: offset };
      }
    });

    function polarToCartesian(centerX, centerY, radius, angleInRadians) {
      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    }

    function describeArc(startAngle, endAngle) {
      const outerR = props.radius;
      const innerR = innerRadius.value;
      const outerStart = polarToCartesian(
        center.value.x,
        center.value.y,
        outerR,
        startAngle,
      );
      const outerEnd = polarToCartesian(
        center.value.x,
        center.value.y,
        outerR,
        endAngle,
      );
      const innerStart = polarToCartesian(
        center.value.x,
        center.value.y,
        innerR,
        startAngle,
      );
      const innerEnd = polarToCartesian(
        center.value.x,
        center.value.y,
        innerR,
        endAngle,
      );

      const largeArcFlag = endAngle - startAngle <= Math.PI ? '0' : '1';

      return [
        'M',
        outerStart.x,
        outerStart.y,
        'A',
        outerR,
        outerR,
        0,
        largeArcFlag,
        1,
        outerEnd.x,
        outerEnd.y,
        'L',
        innerEnd.x,
        innerEnd.y,
        'A',
        innerR,
        innerR,
        0,
        largeArcFlag,
        0,
        innerStart.x,
        innerStart.y,
        'Z',
      ].join(' ');
    }

    function getPath(index) {
      const n = selectableItems.value.length;
      const sliceAngle = Math.PI / 2 / n; // Quarter circle for corner menu
      const startAngle = startAngleOffset.value + index * sliceAngle;
      const endAngle = startAngle + sliceAngle;
      return describeArc(startAngle, endAngle);
    }

    function getIconPosition(index) {
      const n = selectableItems.value.length;
      const sliceAngle = Math.PI / 2 / n;
      const angle
        = startAngleOffset.value + index * sliceAngle + sliceAngle / 2;
      const midRadius = (props.radius + innerRadius.value) / 2;
      return polarToCartesian(center.value.x, center.value.y, midRadius, angle);
    }

    function toggleMenu() {
      isOpen.value = !isOpen.value;
    }

    function handleClick(item) {
      isOpen.value = false;

      // Update store based on mode
      if (props.mode === 'drawing') {
        viewportStateStore.setDrawingMode(props.viewportId, item.value);
      }
      else if (props.mode === 'tool') {
        viewportStateStore.setToolMode(props.viewportId, item.value);
      }
    }

    const selectedItemIcon = computed(() => {
      if (
        props.mode === 'tool'
        && selectedItem.value?.value === ViewportToolMode.Contour
      ) {
        return currentDrawingContourIcon.value || selectedItem.value?.icon;
      }
      return selectedItem.value?.icon;
    });

    return {
      items,
      selectableItems,
      getPath,
      getIconPosition,
      handleClick,
      closeButtonPosition,
      closeButtonRadius,
      isOpen,
      selectedItem,
      selectedItemIcon,
      toggleMenu,
      showDrawingTools,
    };
  },
});
</script>

<template>
  <template v-if="showDrawingTools">
    <svg :width="radius" :height="radius" class="corner-menu">
      <template v-if="isOpen">
        <template v-for="(item, index) in selectableItems" :key="index">
          <!-- Render each ring segment with tooltip -->
          <g class="segment-group">
            <path
              :d="getPath(index)"
              class="ring-segment"
              @click.stop.prevent="handleClick(item)"
              @mousedown.stop.prevent
            >
              <title>{{ item.label }}</title>
            </path>
            <!-- Always render icon, use default if not provided -->
            <image
              :href="item.icon || defaultIcon"
              :x="getIconPosition(index).x - iconSize / 2"
              :y="getIconPosition(index).y - iconSize / 2"
              :width="iconSize"
              :height="iconSize"
            >
              <title>{{ item.label }}</title>
            </image>
          </g>
        </template>
      </template>
      <!-- Selected option button -->
      <g
        class="selected-button"
        @click.stop.prevent="toggleMenu"
        @mousedown.stop.prevent
      >
        <circle
          :cx="closeButtonPosition.x"
          :cy="closeButtonPosition.y"
          :r="closeButtonRadius"
          class="selected-circle"
        >
          <title>{{ selectedItem?.label || 'Select Option' }}</title>
        </circle>
        <image
          :href="selectedItemIcon || defaultIcon"
          :x="closeButtonPosition.x - iconSize / 2"
          :y="closeButtonPosition.y - iconSize / 2"
          :width="iconSize"
          :height="iconSize"
        >
          <title>{{ selectedItem?.label || 'Select Option' }}</title>
        </image>
      </g>
    </svg>
  </template>
</template>

<style scoped>
.corner-menu {
  cursor: default;
  position: relative;
  z-index: 1000;
  pointer-events: none;
  /* Force capturing all pointer events */
  user-select: none;
  /* Prevent text selection */
}

.ring-segment {
  cursor: pointer;
  pointer-events: all;
  stroke: var(--md-border);
  stroke-width: 1;
  transition: fill 0.3s;
  fill: var(--glass-bg-tertiary);
  /* Default light grey color */
}

.segment-group:hover .ring-segment {
  filter: brightness(1.2);
}

.segment-group image {
  transition: transform 0.2s ease-out;
  pointer-events: none;
  transform-origin: center;
}

.segment-group:hover image {
  transform: scale(1.02);
}

.selected-button {
  cursor: pointer;
  pointer-events: all;
}

.selected-circle {
  transition: fill 0.3s;
  fill: var(--glass-bg-tertiary);
}

.selected-button:hover .selected-circle {
  fill: var(--glass-bg-tertiary);
}

.selected-button image {
  pointer-events: none;
  transition: transform 0.2s ease-out;
  transform-origin: center;
}

.selected-button:hover image {
  transform: scale(1.02);
}
</style>
